import { useEffect, useState, useCallback } from "react";
import {
  employeeSignIn,
  employeeSignOut,
  getMyAttendanceHistory,
} from "../../api/attendanceApi";
import { getMyLeaveBalance } from "../../api/leaveApi";
import { getEmployeeDashboardHighlights } from "../../api/employeeApi";
import Loader from "../../components/Loader";
import PremiumLeaveCard from "../../components/PremiumLeaveCard";
import { useLocation } from "react-router-dom";

const formatDateDMY = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${day}/${month}/${year}`;
};

const formatDateTime = (dt) => {
  if (!dt) return "-";
  const d = new Date(dt.replace(" ", "T"));
  if (isNaN(d.getTime())) return dt;
  return d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const convertToHours = (timeStr) => {
  if (!timeStr) return 0;
  const h = timeStr.match(/(\d+)h/);
  const m = timeStr.match(/(\d+)m/);
  const s = timeStr.match(/(\d+)s/);
  const hours = h ? parseInt(h[1]) : 0;
  const minutes = m ? parseInt(m[1]) : 0;
  const seconds = s ? parseInt(s[1]) : 0;
  return hours + minutes / 60 + seconds / 3600;
};

const EmployeeDashboard = () => {
  const location = useLocation();
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const [totalHours, setTotalHours] = useState(0);
  const [attendanceSummary, setAttendanceSummary] = useState({
    present: 0,
    absent: 0,
    halfDay: 0,
  });
  const [leaveCards, setLeaveCards] = useState({});
  const [employees, setEmployees] = useState({
    birthdays: [],
    anniversaries: [],
    new_joiners: [],
  });
  const [leaveSummary, setLeaveSummary] = useState({
    total: 0,
    taken: 0,
    balance: 0,
  });

  /* ================= HELPERS ================= */

  const formatTime = () => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}h ${m}m ${s}s`;
  };

  /* ================= LOAD ATTENDANCE ================= */

  const loadAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyAttendanceHistory();
      const data = res.data || [];

      let total = 0;
      let presentCount = 0;
      let absentCount = 0;
      let halfDayCount = 0;

      data.forEach((r) => {
        total += convertToHours(r.working_hours || "0h 0m");
        if (r.status === "PRESENT") presentCount++;
        else if (r.status === "ABSENT") absentCount++;
        else if (r.status === "HALF_DAY") halfDayCount++;
      });
      setTotalHours(total.toFixed(2));
      setAttendanceSummary({ present: presentCount, absent: absentCount, halfDay: halfDayCount });

      const todayDate = new Date().toISOString().slice(0, 10);
      const todayRecord = data.find((r) => r.date === todayDate);
      setToday(todayRecord || null);

      if (todayRecord?.sign_in && !todayRecord?.sign_out) {
        const elapsedSecs = Math.floor((Date.now() - new Date(todayRecord.sign_in)) / 1000);
        if (elapsedSecs >= 43200) {
          setWorking(false);
          setSeconds(43200);
        } else {
          setWorking(true);
          setSeconds(elapsedSecs);
        }
      } else if (todayRecord?.sign_in && todayRecord?.sign_out) {
        setWorking(false);
        const diffSecs = Math.floor(
          (new Date(todayRecord.sign_out) - new Date(todayRecord.sign_in)) / 1000
        );
        setSeconds(Math.min(diffSecs, 43200));
      } else {
        setWorking(false);
        setSeconds(0);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  /* ================= LOAD LEAVE ================= */

  const loadLeaveSummary = useCallback(async () => {
    try {
      const res = await getMyLeaveBalance();
      const data = res.data || {};
      setLeaveSummary({
        total: data.total || 0,
        taken: data.taken || 0,
        balance: data.balance || 0,
      });
      const cards = {};
      Object.keys(data).forEach((key) => {
        if (key !== "total" && key !== "taken" && key !== "balance") {
          cards[key] = data[key];
        }
      });
      setLeaveCards(cards);
    } catch (err) {
      console.error(err);
    }
  }, []);

  /* ================= LOAD HIGHLIGHTS ================= */

  const loadEmployees = useCallback(async () => {
    try {
      const res = await getEmployeeDashboardHighlights();
      setEmployees(res.data);
    } catch {
      setEmployees({ birthdays: [], anniversaries: [], new_joiners: [] });
    }
  }, []);

  useEffect(() => {
    loadAttendance();
    loadLeaveSummary();
    loadEmployees();
  }, [location.pathname, loadAttendance, loadLeaveSummary, loadEmployees]);

  useEffect(() => {
    let timer;
    if (working) {
      timer = setInterval(() => {
        setSeconds((s) => {
          if (s >= 43200) { setWorking(false); return 43200; }
          return s + 1;
        });
      }, 1000);
    }
    return () => timer && clearInterval(timer);
  }, [working]);

  /* ================= ACTIONS ================= */

  const handleSignIn = async () => {
    if (actionLoading) return;
    if (!window.confirm("Are you sure you want to sign in?")) return;
    setActionLoading(true);
    try {
      await employeeSignIn();
      await loadAttendance();
    } finally {
      setActionLoading(false);
    }
  };

  const handleSignOut = async () => {
    if (actionLoading) return;
    if (!window.confirm("Are you sure you want to sign out?")) return;
    setActionLoading(true);
    try {
      await employeeSignOut();
      setWorking(false);
      await loadAttendance();
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="space-y-8">

      {/* ================= TOP SUMMARY CARDS ================= */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <SummaryCard title="Total Leaves" value={leaveSummary.total} icon="📋"
          accent="from-sky-500 to-cyan-500" bg="from-sky-50 to-cyan-50" border="border-sky-100" />
        <SummaryCard title="Leaves Taken" value={leaveSummary.taken} icon="✈️"
          accent="from-rose-500 to-pink-500" bg="from-rose-50 to-pink-50" border="border-rose-100" />
        <SummaryCard title="Balance Leaves" value={leaveSummary.balance} icon="🏖️"
          accent="from-violet-500 to-purple-500" bg="from-violet-50 to-purple-50" border="border-violet-100" />
        <SummaryCard title="Total Hours" value={`${totalHours} hrs`} icon="⏱️"
          accent="from-emerald-500 to-teal-500" bg="from-emerald-50 to-teal-50" border="border-emerald-100" />
      </div>

      {/* ================= LEAVE BALANCE CARDS ================= */}
      {Object.keys(leaveCards).length > 0 && (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {Object.keys(leaveCards).map((key) => {
              const card = leaveCards[key];
              const displayTitle = key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
              const n = displayTitle.toLowerCase();
              let colorType = "default";
              if (n.includes("casual")) colorType = "casual";
              else if (n.includes("sick")) colorType = "sick";
              else if (n.includes("compensatory") || n.includes("comp")) colorType = "compensatory";
              else if (n.includes("loss") || n.includes("lop")) colorType = "taken";
              return (
                <PremiumLeaveCard key={key} title={displayTitle}
                  allowed={card?.total ?? 0} used={card?.used ?? 0} color={colorType} />
              );
            })}
          </div>
        </div>
      )}


      {/* ================= TODAY ATTENDANCE ================= */}
      <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xl">📅</span>
            <h3 className="text-base font-bold text-slate-700">Today Attendance</h3>
          </div>
          <StatusBadge status={today?.status || "NOT_MARKED"} />
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
            <InfoCell label="Sign In" value={formatDateTime(today?.sign_in)} icon="🟢" />
            <InfoCell
              label="Sign Out" icon="🔴"
              value={
                <>
                  {formatDateTime(today?.sign_out)}
                  {today?.is_auto_signout && (
                    <span className="ml-2 text-[10px] font-bold bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">Auto</span>
                  )}
                </>
              }
            />
            <InfoCell label="Working Time" value={formatTime()} icon="⏱" />
          </div>
          <div className="flex gap-3">
            {!today?.sign_in && (
              <button disabled={actionLoading} onClick={handleSignIn}
                className="px-6 py-2.5 rounded-full text-white font-semibold text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 shadow hover:shadow-md transition-all duration-200 flex items-center gap-2">
                {actionLoading
                  ? <><span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full inline-block animate-spin" /> Signing In...</>
                  : <><span>🟢</span> Sign In</>}
              </button>
            )}
            {today?.sign_in && !today?.sign_out && (
              <button disabled={actionLoading} onClick={handleSignOut}
                className="px-6 py-2.5 rounded-full text-white font-semibold text-sm bg-rose-600 hover:bg-rose-700 disabled:opacity-60 shadow hover:shadow-md transition-all duration-200 flex items-center gap-2">
                {actionLoading
                  ? <><span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full inline-block animate-spin" /> Signing Out...</>
                  : <><span>🔴</span> Sign Out</>}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ================= HIGHLIGHTS ================= */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <HighlightCard icon="🎂" title="Birthdays This Month" accentColor="pink"
          items={employees.birthdays.map((emp) => ({ key: emp.id, name: emp.full_name, badge: formatDateDMY(emp.date) }))}
          emptyText="No birthdays this month" />
        <HighlightCard icon="🏆" title="Work Anniversaries" accentColor="amber"
          items={employees.anniversaries.map((emp) => ({ key: emp.id, name: emp.full_name, badge: `${emp.years}y` }))}
          emptyText="No anniversaries this month" />
        <HighlightCard icon="🎉" title="New Joiners" accentColor="blue"
          items={employees.new_joiners.map((emp) => ({ key: emp.id, name: emp.full_name, badge: "New" }))}
          emptyText="No new joiners" />
      </div>
    </div>
  );
};

/* ================= REUSABLE COMPONENTS ================= */

const SummaryCard = ({ title, value, icon, accent, bg, border }) => (
  <div className={`relative overflow-hidden bg-gradient-to-br ${bg} border ${border} p-5 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{title}</p>
        <h2 className="text-3xl font-extrabold text-slate-800 mt-2 tracking-tight">{value}</h2>
      </div>
      <span className="text-2xl">{icon}</span>
    </div>
    <div className={`absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r ${accent} opacity-60 rounded-b-2xl`} />
  </div>
);

const AttendanceStat = ({ label, value, color, icon }) => {
  const colors = {
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
    rose: "bg-rose-50 border-rose-100 text-rose-700",
    amber: "bg-amber-50 border-amber-100 text-amber-700",
    blue: "bg-blue-50 border-blue-100 text-blue-700",
  };
  return (
    <div className={`flex items-center gap-4 p-4 rounded-2xl border ${colors[color]} transition-all hover:shadow-sm`}>
      <span className="text-2xl">{icon}</span>
      <div>
        <p className="text-xs font-bold uppercase tracking-wider opacity-70">{label}</p>
        <p className="text-2xl font-extrabold mt-0.5">{value}</p>
      </div>
    </div>
  );
};

const InfoCell = ({ label, value, icon }) => (
  <div className="flex items-start gap-3">
    <span className="text-lg mt-0.5">{icon}</span>
    <div>
      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">{label}</p>
      <div className="text-sm font-semibold text-slate-700 mt-1">{value || "—"}</div>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    PRESENT: { cls: "bg-emerald-50 text-emerald-700 border border-emerald-200", dot: "bg-emerald-500" },
    ABSENT: { cls: "bg-rose-50 text-rose-700 border border-rose-200", dot: "bg-rose-500" },
    HALF_DAY: { cls: "bg-amber-50 text-amber-700 border border-amber-200", dot: "bg-amber-500" },
    NOT_MARKED: { cls: "bg-slate-50 text-slate-500 border border-slate-200", dot: "bg-slate-400" },
  };
  const config = map[status] || map.NOT_MARKED;
  return (
    <span className={`px-3 py-1 text-xs font-semibold rounded-full inline-flex items-center gap-1.5 ${config.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {status.replace(/_/g, " ")}
    </span>
  );
};

const HighlightCard = ({ icon, title, items, emptyText, accentColor }) => {
  const accents = {
    pink: "from-pink-500 to-rose-500",
    amber: "from-amber-500 to-orange-500",
    blue: "from-blue-500 to-indigo-500",
  };
  const badges = {
    pink: "bg-pink-50 text-pink-700",
    amber: "bg-amber-50 text-amber-700",
    blue: "bg-indigo-50 text-indigo-700",
  };
  return (
    <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-5 py-4 flex items-center gap-3 border-b border-slate-100">
        <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${accents[accentColor]} flex items-center justify-center text-sm`}>{icon}</div>
        <h3 className="font-bold text-slate-700 text-sm">{title}</h3>
        <span className="ml-auto text-xs font-bold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{items.length}</span>
      </div>
      <div className="p-4 space-y-2.5 max-h-48 overflow-y-auto">
        {items.length === 0 ? (
          <p className="text-slate-400 text-sm text-center py-3">{emptyText}</p>
        ) : (
          items.map((item) => (
            <div key={item.key} className="flex items-center justify-between py-1">
              <div className="flex items-center gap-2">
                <span className={`w-7 h-7 rounded-full bg-gradient-to-br ${accents[accentColor]} text-white text-xs font-bold flex items-center justify-center`}>
                  {item.name?.[0]?.toUpperCase() || "?"}
                </span>
                <span className="text-sm font-medium text-slate-700">{item.name}</span>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badges[accentColor]}`}>{item.badge}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default EmployeeDashboard;